/**
 * Requisitions list page — shows all operational requisitions submitted via the
 * Data Entry Center, with status badges (Under Review / Approved / Rejected).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ClipboardList, Eye, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  under_review: { label: 'Under Review', icon: Clock, color: 'bg-orange-50 text-orange-700 border-orange-200' },
  approved:     { label: 'Approved',     icon: CheckCircle2, color: 'bg-green-50 text-green-700 border-green-200' },
  rejected:     { label: 'Rejected',     icon: XCircle, color: 'bg-red-50 text-red-700 border-red-200' },
};

const RequisitionsPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [mineOnly, setMineOnly] = useState(user?.role !== 'super_admin');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { mine_only: mineOnly };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API_URL}/api/requisitions`, { params });
      setItems(res.data.items || []);
    } catch (e) { toast.error('Failed to load requisitions'); }
    finally { setLoading(false); }
  }, [mineOnly, statusFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ClipboardList className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-heading font-bold">Operational Requisitions</h1>
          <p className="text-sm text-muted-foreground">Track requisitions through review and approval.</p>
        </div>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0 flex-wrap gap-2">
          <CardTitle className="text-base">{items.length} requisition{items.length !== 1 && 's'}</CardTitle>
          <div className="flex gap-2 flex-wrap">
            {isSuperAdmin && (
              <Select value={mineOnly ? 'mine' : 'all'} onValueChange={(v) => setMineOnly(v === 'mine')}>
                <SelectTrigger className="w-32" data-testid="req-scope-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users</SelectItem>
                  <SelectItem value="mine">My requests</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="req-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Requester</TableHead>
                <TableHead>Job Title</TableHead>
                <TableHead>Items</TableHead>
                <TableHead className="text-right">Total (UGX)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
              : items.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">No requisitions yet</TableCell></TableRow>
              : items.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.under_review;
                const Icon = meta.icon;
                return (
                  <TableRow key={r.id} data-testid={`req-row-${r.id}`}>
                    <TableCell className="text-xs">{r.request_date}</TableCell>
                    <TableCell>{r.requester_first_name} {r.requester_last_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.job_title}</TableCell>
                    <TableCell className="text-xs">{(r.items || []).length} item(s)</TableCell>
                    <TableCell className="text-right tabular-nums">{(r.total_cost || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={meta.color}>
                        <Icon className="h-3 w-3 mr-1 inline" /> {meta.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => setDetail(r)} data-testid={`req-view-${r.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Requisition Detail</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-muted-foreground">Requester:</span> <strong>{detail.requester_first_name} {detail.requester_last_name}</strong></div>
                <div><span className="text-muted-foreground">Job Title:</span> {detail.job_title}</div>
                <div><span className="text-muted-foreground">Date:</span> {detail.request_date}</div>
                <div>
                  <span className="text-muted-foreground">Status:</span>{' '}
                  <Badge variant="outline" className={(STATUS_META[detail.status] || STATUS_META.under_review).color}>
                    {(STATUS_META[detail.status] || STATUS_META.under_review).label}
                  </Badge>
                </div>
                <div><span className="text-muted-foreground">Budget Code:</span> <span className="font-mono text-xs">{detail.budget_code || '—'}</span></div>
                <div>
                  <span className="text-muted-foreground">Budget Status:</span>{' '}
                  <Badge variant="outline" className={
                    detail.budget_status === 'within_budget' ? 'bg-green-50 text-green-700 border-green-200' :
                    detail.budget_status === 'over_budget' ? 'bg-red-50 text-red-700 border-red-200' :
                    'bg-amber-50 text-amber-700 border-amber-200'
                  }>{(detail.budget_status || '').replace(/_/g, ' ')}</Badge>
                </div>
              </div>
              <div>
                <div className="font-semibold text-xs uppercase text-muted-foreground mb-1">Justification</div>
                <p className="bg-muted/30 p-2 rounded text-sm">{detail.justification || '—'}</p>
              </div>
              <div>
                <div className="font-semibold text-xs uppercase text-muted-foreground mb-1">Items</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Part # / Model</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Unit Cost</TableHead>
                      <TableHead className="text-right">Line Total</TableHead>
                      <TableHead>Specifications</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detail.items || []).map((it, i) => (
                      <TableRow key={`${detail.id || 'req'}-item-${it.part_number || it.name || i}`}>
                        <TableCell>{it.name}</TableCell>
                        <TableCell className="text-xs font-mono">{it.part_number || '—'}</TableCell>
                        <TableCell className="text-right">{it.quantity}</TableCell>
                        <TableCell>{it.unit_of_measure || '—'}</TableCell>
                        <TableCell className="text-right tabular-nums">{(it.unit_cost || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-right tabular-nums">{(it.line_total || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-xs">{it.specifications || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="text-right mt-2 font-medium">Total: UGX {(detail.total_cost || 0).toLocaleString()}</div>
              </div>
              {detail.rejected_reason && (
                <div className="bg-red-50 p-2 rounded text-sm border border-red-200">
                  <strong className="text-red-700">Rejection reason:</strong> {detail.rejected_reason}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequisitionsPage;
